<template>
	<k-dialog
		light
		v-if="shown"
		content-class="simple-dialog"
		:fullscreen="fullscreen"
		:aria-labelledby="message.title ? dialog_title_id : undefined"
		:initial-focus-selector="initial_focus_selector"
		:restore-focus-element="message.focusOriginEl"
		eager
		v-model="shown"
		:max-width="max_width"
		scrollable
		persistent
		overlay-opacity="0.9"
		@escape="cancel"
	>
		<k-card>
			<k-card-title v-if="message.title">
				<h3 :id="dialog_title_id" v-html="message.title"></h3>
			</k-card-title>
			<k-card-text>
				<div v-html="message.text"></div>
				<k-text-field
					ref="passwordInput"
					dense
					outlined
					hide-details="auto"
					class="simple-dialog-text-field"
					v-if="type === 'prompt' && message.promptType === 'password'"
					:value="prompt.value"
					@input="prompt_handle_input"
					@blur="prompt_handle_blur"
					:error-messages="prompt_error_messages"
					@keyup="checkSubmit"
					autocomplete="new-password"
					:type="show_password ? 'text' : 'password'"
					:append-icon="show_password ? 'fas fa-eye' : 'fas fa-eye-slash'"
					:append-aria-label="show_password ? 'Hide password' : 'Show password'"
					:append-aria-pressed="show_password"
					@click:append="show_password = !show_password"
				></k-text-field>
				<k-text-field
					ref="textInput"
					dense
					outlined
					hide-details="auto"
					class="simple-dialog-text-field"
					v-if="type === 'prompt' && message.promptType === 'text'"
					:type="message.type || 'text'"
					:value="prompt.value"
					@input="prompt_handle_input"
					@blur="prompt_handle_blur"
					:error-messages="prompt_error_messages"
					@keyup="checkSubmit"
				></k-text-field>
				<k-textarea
					ref="textareaInput"
					dense
					outlined
					hide-details="auto"
					auto-grow
					class="simple-dialog-textarea"
					v-if="type === 'prompt' && message.promptType === 'textarea'"
					:value="prompt.value"
					@input="prompt_handle_input"
					@blur="prompt_handle_blur"
					:error-messages="prompt_error_messages"
				></k-textarea>
				<k-file-input
					ref="fileInput"
					dense
					outlined
					hide-details="auto"
					placeholder="Select a file…"
					class="simple-dialog-file-input"
					v-if="type === 'prompt' && message.promptType === 'file'"
					:value="prompt.value"
					@change="prompt_handle_input"
					@blur="prompt_handle_blur"
					:error-messages="prompt_error_messages"
				></k-file-input>
				<k-autocomplete
					ref="autocompleteInput"
					dense
					outlined
					hide-details="auto"
					auto-grow
					class="simple-dialog-autocomplete"
					v-if="type === 'prompt' && message.promptType === 'autocomplete'"
					:value="prompt.value"
					@input="prompt_handle_input"
					@blur="prompt_handle_blur"
					:items="autocomplete_items"
					:loading="autocomplete_loading"
					:search-input.sync="autocomplete_search"
					:error-messages="prompt_error_messages"
					hide-no-data
					hide-selected
				></k-autocomplete>
				<k-select
					ref="selectInput"
					dense
					outlined
					hide-details="auto"
					class="simple-dialog-select-input"
					v-if="type === 'prompt' && message.promptType === 'select'"
					:value="prompt.value"
					@input="prompt_handle_input"
					@blur="prompt_handle_blur"
					:items="message.selectOptions"
					:error-messages="prompt_error_messages"
					label=""
				></k-select>

				<div
					v-if="message.secondaryCheckbox"
					class="mt-3"
				>
					<k-checkbox
						class="mt-0 pt-0"
						v-model="checkbox_value"
						hide-details
					><template v-slot:label
					><div
						style="color: #000"
						v-html="message.secondaryCheckbox"
					></div></template
					></k-checkbox>
				</div>
			</k-card-text>
			<k-card-actions>
				<k-btn
					v-on:click="cancel"
					class="simple-dialog-dismiss-btn"
					dark
					:color="message.cancelColor"
					v-if="type !== 'alert'"
					:style="message.hideCancel == true ? 'visibility:hidden;height:0px;' : ''"
				>
					<k-icon
						v-if="message.cancelIcon"
						small
						class="mr-2"
					>{{ message.cancelIcon }}</k-icon
					>
					<span v-html="message.cancelText"></span>
					<k-icon
						v-if="message.cancelIconAfter"
						small
						class="ml-2"
					>{{ message.cancelIconAfter }}</k-icon
					>
				</k-btn>
				<k-spacer></k-spacer>
				<k-btn
					v-if="message.extraBtnText"
					v-on:click="extra_btn_clicked"
					dark
					:color="message.extraBtnColor"
					ref="extraBtn"
					class="mr-2"
				>
					<k-icon
						v-if="message.extraBtnIcon"
						small
						class="mr-2"
					>{{ message.extraBtnIcon }}</k-icon
					>
					<span v-html="message.extraBtnText"></span>
					<k-icon
						v-if="message.extraBtnIconAfter"
						small
						class="ml-2"
					>{{ message.extraBtnIconAfter }}</k-icon
					>
				</k-btn>

				<k-btn
					v-on:click="accept"
					:class="type == 'alert' ? 'simple-dialog-dismiss-btn' : ''"
					dark
					:color="message.acceptColor"
					:style="message.hideAccept == true ? 'visibility:hidden;height:0px;' : ''"
					:disabled="accept_disabled"
					ref="acceptBtn"
				>
					<k-icon
						v-if="message.acceptIcon"
						small
						class="mr-2"
					>{{ message.acceptIcon }}</k-icon
					>
					<span v-html="message.acceptText"></span>
					<k-icon
						v-if="message.acceptIconAfter"
						small
						class="ml-2"
					>{{ message.acceptIconAfter }}</k-icon
					>
				</k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>
</template>

<script>
import KDialog from '../k-dialog/KDialog.vue';
import KBtn from '../k-btn/KBtn.vue';
import KCard from '../k-card/KCard.vue';
import KCardTitle from '../k-card-title/KCardTitle.vue';
import KCardText from '../k-card-text/KCardText.vue';
import KCardActions from '../k-card-actions/KCardActions.vue';
import KIcon from '../k-icon/KIcon.vue';
import KSpacer from '../k-spacer/KSpacer.vue';
import KCheckbox from '../k-checkbox/KCheckbox.vue';
import KAutocomplete from '../k-autocomplete/KAutocomplete.vue';
import KTextField from '../k-text-field/KTextField.vue';
import KTextarea from '../k-textarea/KTextarea.vue';
import KFileInput from '../k-file-input/KFileInput.vue';
import KSelect from '../k-select/KSelect.vue';
import { usePromptMixin } from '../../mixins/usePromptMixin';


export default {
	name: 'KDialogSimple',
	components: {
		KDialog,
		KBtn,
		KCard,
		KCardTitle,
		KCardText,
		KCardActions,
		KSpacer,
		KCheckbox,
		KIcon,
		KAutocomplete,
		KTextField,
		KTextarea,
		KFileInput,
		KSelect
	},
	mixins: [usePromptMixin()],
	props: {
		type: String,
		message: Object,
		resolve: Function,
	},
	data() {
		return {
			shown: false,
			dialog_title_id: `k-simple-dialog-title-${Math.random().toString(36).slice(2, 10)}`,
			autocomplete_loading: false,
			autocomplete_search: '',
			autocomplete_items: [],
			checkbox_value: false,
			show_password: false
		};
	},
	created() {
		if (this.type === 'prompt') {
			const initial = this.message.promptType === 'file'
				? []
				: (this.message.initialValue != null ? this.message.initialValue : '');
			this.prompt_set_schema(this.message.schema, initial);
		}
		if (this.message.secondaryCheckboxInitiallyOn) this.checkbox_value = true;
	},
	computed: {
		fullscreen() {
			return this.message.fullscreen === true;
		},
		initial_focus_selector() {
			if (this.message.focusBtn) return 'button'
			if (this.type !== 'prompt') return 'button, [href], input:not([type="hidden"]), select, textarea'
			if (this.message.promptType === 'password') return '.simple-dialog-text-field input'
			if (this.message.promptType === 'text') return '.simple-dialog-text-field input'
			if (this.message.promptType === 'textarea') return '.simple-dialog-textarea textarea'
			if (this.message.promptType === 'file') return '.simple-dialog-file-input .v-input__icon--prepend > button'
			if (this.message.promptType === 'autocomplete') return '.simple-dialog-autocomplete input'
			if (this.message.promptType === 'select') return '.simple-dialog-select-input input, .simple-dialog-select-input select, button'
			return 'button, [href], input:not([type="hidden"]), select, textarea'
		},
		max_width() {
			if (this.$vuetify.breakpoint.xs) return '100vw';
			return this.message.dialogMaxWidth || 500;
		},
		accept_disabled() {
			if (this.message.disableForEmptyValue) {
				const v = this.prompt.value;
				return v == null || v === '' || (Array.isArray(v) && v.length === 0);
			}
			return false;
		},
		prompt_error_messages() {
			return this.prompt.error ? [this.prompt.error] : [];
		}
	},
	watch: {
		autocomplete_search(val) {
			/*
					To use autocomplete, either specify `autocompleteItems` in the $prompt call (they should be an array of strings),
					or specify a `serviceName` value, in which case:
						- this fn will call serviceName to retrieve the items based on the first 3 characters the user types
						- the service must return an array of `entries` in the result
						- caller can optionally specify `entryField` to use a field of the returned objects;
						  if entryField isn't specified, the service will receive `return_string:yes`, which should signal it to return an array of strings
				*/

			// if we get autocompleteItems...
			if (this.message.autocompleteItems) {
				// if requiredAutocompleteCharacters parameter is included, require the user to type at least that many characters before the list appears
				if (
					this.message.requiredAutocompleteCharacters &&
          (!val || val.length < this.message.requiredAutocompleteCharacters)
				)
					this.autocomplete_items = [];
				else this.autocomplete_items = this.message.autocompleteItems;
				return;
			}

			// if we didn't get autocompleteItems, we need a service to call, and we will debounce
			if (!this.message.serviceName) return;

			// establish the debounce fn if necessary
			if (empty(this.fn_debounced)) {
				this.fn_debounced = U.debounce(val => {
					// if we're using a service, don't search until we have three characters; then if the value drops back below three characters, clear autocomplete_items so we'll search again when we get back to 3
					if (!val || val.length < 3) {
						this.autocomplete_items = [];
						return;

						// else if the previous service call returned items and the current val is an extension of the previous val, we don't have to re-search
					} else if (this.autocomplete_items.length > 0) {
						if (this.previous_search_val && val.indexOf(this.previous_search_val) == 0) {
							console.log(`autocomplete_search returning: ${this.previous_search_val} / ${val}`);
							return;
						}
					}
					console.log(`autocomplete_search running: ${val}`);

					// if we get to here, search for items
					this.previous_search_val = val;
					let payload = {
						user_id: vapp.user_info.user_id,
						// if an entryField is specified, we want the service to return an object; otherwise tell it to return a string
						return_string: this.message.entryField ? 'no' : 'yes',
						stem: val
					};
					U.ajax(this.message.serviceName, payload, result => {
						// parse results, which should be in result.entries
						let arr = [];
						for (let entry of result.entries) {
							// if entryField is specified, entries should be objects; get the right field
							if (this.message.entryField) arr.push(entry[this.message.entryField]);
							// else entries should be strings
							else arr.push(entry);
						}
						this.autocomplete_items = arr;
					});
				}, 500);
			}
			// call the debounce fn
			this.fn_debounced(val);
		}
	},
	methods: {
		restore_focus_origin() {
			// Promise dialogs can unmount immediately after resolving, so restore
			// focus here as a fallback to keep keyboard users anchored.
			const focus_origin = this.message.focusOriginEl
			if (!focus_origin?.focus) return
			this.$nextTick(() => {
				window.setTimeout(() => focus_origin.focus(), 0)
			})
		},
		remove_overlay() {
			// our version of vuetify doesn't remove v-overlay divs when dialogs are closed (at least the way we do them here), do remove them manually here
			// note that we only want to remove the FIRST active overlay (we could have multiple dialogs open on top of each other)
			const element = document.querySelector('.v-overlay--active');
			if (element) {
				element.remove();
			}


		},

		// Note: if caller asked for the checkbox, send an array with the checkbox_value the second item in the array, if the user clicked the accept or extra btn
		cancel() {
			if (this.message.rejectCloseOnClick) this.shown = false;
			// if they click the cancel btn, we have to return false for the resolve fn to work properly (this could be updated if we really need to)
			this.remove_overlay();
			this.restore_focus_origin();
			this.resolve(false);
		},
		accept() {
			if (this.type == 'prompt') {
				if (this.message.promptType === 'autocomplete' && !this.prompt.value) {
					this.prompt_handle_input(this.autocomplete_search);
				}

				if (this.message.schema && !this.prompt_validate()) {
					return;
				}
			}

			if (this.message.resolveCloseOnClick) this.shown = false;
			if (this.type == 'prompt') {
				const value = this.prompt.value;
				const rv = this.message.secondaryCheckbox ? [value, this.checkbox_value] : value;
				this.remove_overlay();
				this.restore_focus_origin();
				this.resolve(rv);
			} else {
				const rv = this.message.secondaryCheckbox ? [true, this.checkbox_value] : true;
				this.remove_overlay();
				this.restore_focus_origin();
				this.resolve(rv);
			}
		},
		extra_btn_clicked() {
			// extra btn option -- allows for a choice of two things with an additional cancel option -- only used with 'confirm'
			if (this.message.resolveCloseOnClick) this.shown = false;
			let rv = this.message.secondaryCheckbox ? ['extra', this.checkbox_value] : 'extra';
			this.remove_overlay();
			this.restore_focus_origin();
			this.resolve(rv);
			// caller should look for `x === 'extra'` as the return value
		},
		checkSubmit(evt) {
			if (evt.key === 'Enter') {
				this.accept();
			}

			// if allCaps is true, transform input to upper case
			if (this.message.allCaps) {
				this.prompt_handle_input((this.prompt.value || '').toUpperCase());
			}
		}
	},
	mounted() {
		this.shown = true;
		if (this.type === 'prompt') {
			setTimeout(
				function () {
					if (this.message.promptType == 'text') {
						this.$refs.textInput.$el.querySelector('input').focus();
						this.$refs.textInput.$el.querySelector('input').select();
					} else if (this.message.promptType == 'textarea') {
						this.$refs.textareaInput.$el.querySelector('textarea').focus();
						this.$refs.textareaInput.$el.querySelector('textarea').select();
					} else if (this.message.promptType == 'file') {
						const file_button = this.$refs.fileInput?.$el?.querySelector('.v-input__icon--prepend > button');
						file_button?.focus();
					}
				}.bind(this),
				300
			);
		}

		if (this.message.focusBtn) {
			setTimeout(
				function () {
					this.$refs.acceptBtn.$el.focus();
				}.bind(this),
				300
			);
		}
	}
};
</script>

<style>
.simple-dialog .v-card {
  background-color: #f8f8f8 !important;
}

.simple-dialog-textarea textarea {
  margin-top: 0 !important;
}

.simple-dialog-text-field {
  margin-top: 8px !important;
}

.simple-dialog-file-input {
  margin-top: 15px !important;
}

.simple-dialog-autocomplete {
  margin-top: 8px !important;
}

.simple-dialog .v-card__text {
  font-size: 16px;
  line-height: 21px;
  color: #111 !important;
  padding: 12px !important;
  /* border-color:#ddd;
	border-style:solid;
	border-width:8px 4px; */
}

.simple-dialog .v-card__actions {
  /* background-color:#ddd; */
  padding: 8px 8px 8px 8px;
}

.simple-dialog .v-card__title {
  /* background-color:#ddd; */
  padding: 8px 12px 0 12px !important;
  word-break: normal;
}
.simple-dialog .v-card__title h3 {
  font-size: 24px;
  margin: 0;
  padding: 0;
  color: #111 !important;
}

.simple-dialog .v-input--checkbox .v-input__slot {
  align-items: flex-start;
}

@media (max-width: 600px) {
  .simple-dialog {
    border-radius: 0 !important;
    min-height: 20vh;
    max-height: 100vh !important;
    margin: 0 4px;
  }

  .simple-dialog .v-card {
    /* border-radius:0!important; */
    /* height:100vh; */
  }

  .simple-dialog .v-card__text {
    font-size: 15px;
    line-height: 22px;
    padding: 8px !important;
  }

  .simple-dialog .v-card__title {
    padding: 8px 8px 0 8px !important;
  }
  .simple-dialog .v-card__title h3 {
    font-size: 20px;
    text-align: center;
  }

  .simple-dialog .v-card__actions {
    flex-wrap: wrap;
  }

  .simple-dialog .v-card__actions .v-btn {
    width: 100%;
    margin-top: 5px;
  }
}
</style>
